import { ApiResponse } from "@apis/api";
import httpClient from "@plugins/http";
import { Notebook, NotebookListingDisplay } from "./notebook-types";

interface Category {
  id: number;
  name: string;
  indexNo: number;
}

interface SearchResult {
  start: number;
  length: number;
  draw: number;
  recordsTotal: number;
  recordsFiltered: number;
  data: Notebook[];
}

export interface CategorizedList {
  hasNextPage: boolean;
  data: NotebookListingDisplay[];
  totalRecords: number;
}

const DEFAULT_SORT_FIELDS = ["modifiedDate", "title.raw"];
const DEFAULT_SORT_DIRECTIONS = ["descending", "ascending"];

export const getCategories = async (): Promise<ApiResponse<Category[]>> => {
  return await httpClient.get("/apis/notebook/category/list");
};

export const createCategory = async (name: string): Promise<ApiResponse<Category>> => {
  return await httpClient.post("/apis/notebook/category/create", { name });
};

//TODO backend change this to post body and not through params
export const renameCategory = async (id: number, name: string): Promise<ApiResponse> => {
  const params = new URLSearchParams();
  params.append("categoryId", id.toString());
  params.append("name", name);

  return await httpClient.post(`/apis/notebook/category/rename?${params}`);
};

//TODO use path variable for the deletion
export const deleteCategory = async (id: number): Promise<ApiResponse> => {
  return await httpClient.post(`/apis/notebook/category/delete?categoryId=${id}`);
};

//TODO use combination of path variable and and post body
export const reorderCategory = async (id: number, isMoveUp: boolean): Promise<ApiResponse> => {
  return await httpClient.post(
    `/apis/notebook/category/reorder?categoryId=${id}&moveUp=${isMoveUp}`,
  );
};

//TODO use combination of path variable and and post body
export const moveCategory = async (
  notebookUuid: string,
  categoryId: number,
): Promise<ApiResponse<string>> => {
  return await httpClient.post(
    `/apis/notebook/move-category/${notebookUuid}?newCategoryId=${categoryId}`,
  );
};

export const removeFromCategory = async (notebookUuid: string): Promise<ApiResponse> => {
  return await httpClient.post(`/apis/notebook/remove-category/${notebookUuid}`);
};

export const search = async (
  keyword: string,
  length: number,
  categoryId?: number,
  start = 0,
  sortField?: string,
  sortDirection?: string,
): Promise<ApiResponse<SearchResult>> => {
  const body = {
    cacheable: false,
    start,
    length,
    keyword,
    categoryId,
    ...processSortCriteria(sortField, sortDirection),
  };
  return await httpClient.post("/apis/notebook/list/search", body);
};

export const getUncategorizedNotebooks = async (
  length: number,
  sortField?: string,
  sortDirection?: string,
): Promise<ApiResponse<NotebookListingDisplay>> => {
  const params = {
    dataResponseLength: length,
    ...processSortCriteria(sortField, sortDirection),
  };

  return await httpClient.get("/apis/notebook/list/uncategorize", {
    params: params,
  });
};

export const getCategorizedNotebooks = async (
  length: number,
  lastCategoryId?: number,
  sortField?: string,
  sortDirection?: string,
): Promise<ApiResponse<CategorizedList>> => {
  const params = {
    dataResponseLength: length,
    ...processSortCriteria(sortField, sortDirection),
    ...(lastCategoryId != undefined && { lastCategoryId }),
  };

  return await httpClient.get("/apis/notebook/list/categorize", {
    params: params,
  });
};

const processSortCriteria = (sortField?: string, sortDirection?: string) => {
  if (sortField && sortDirection) {
    const sortFields = ["title.raw"];
    const sortDirections = ["ascending"];
    sortDirection = sortDirection === "asc" ? "ascending" : "descending";

    if (sortField === "title") {
      sortDirections[0] = sortDirection;
    } else {
      sortFields.unshift(sortField);
      sortDirections.unshift(sortDirection);
    }
    return { sortFields, sortDirections };
  } else {
    return { sortFields: DEFAULT_SORT_FIELDS, sortDirections: DEFAULT_SORT_DIRECTIONS };
  }
};

export const createNotebook = async (
  notebook: Partial<Notebook>,
): Promise<ApiResponse<Notebook>> => {
  return await httpClient.post("/apis/notebook/create", notebook);
};

export const deleteNotebook = async (uuid: string): Promise<ApiResponse<Notebook>> => {
  return await httpClient.post(`/apis/notebook/delete/${uuid}`);
};
